/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PROGRAM CONFIGURATION — the single source of truth
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Every program-specific value in the platform resolves through this module.
 * Nothing else may hardcode a program name, description, document list,
 * eligibility text, terms, pipeline or resource set.
 *
 * WHY THIS EXISTS
 * ---------------
 * The audit found the program model fragmented across the codebase:
 *
 *   1. TWO conflicting `Program` interfaces — `src/types.ts` declared
 *      `terms: string[]` while `src/hooks/usePrograms.ts` declared
 *      `terms: ProgramTerm[]` (objects with isOpen/capacity/dates). Consumers
 *      picked whichever import was nearest, so the same field was read as both
 *      a label list and an intake object list.
 *   2. A hardcoded catalogue of 10 programs (names, descriptions and full
 *      requiredDocuments lists) embedded in ApplicantFlow.tsx as a "fallback".
 *      It was not workspace-scoped, so any tenant whose API call failed saw
 *      another organisation's program catalogue.
 *   3. `requiredDocuments: string[]` — plain labels only. No optional flag, no
 *      description, no accepted formats, no ordering, no validation rules.
 *   4. No program-specific Terms & Conditions field at all (the `terms` field
 *      means academic intakes, not legal terms), and therefore no fallback to
 *      a global default.
 *   5. Training/marketing resources stored as `{id, name, type, url|text}` with
 *      no description, category, ordering, or publish state.
 *
 * THE CONTRACT
 * ------------
 * `normaliseProgram()` accepts ANY historical shape and returns a complete,
 * predictable `ProgramConfig`. Read paths use the `resolve*` helpers, which
 * implement the documented fallback chain:
 *
 *      program value  →  workspace default  →  global default
 *
 * so a screen can never render blank or stale program content.
 */

import type { PipelineStage } from './programPipeline';
import { normalisePipeline } from './programPipeline';
import type { ProgramResource } from './programResources';
import { normaliseResources } from './programResources';
import type { ProgramSkillsConfig } from './programSkills';
import { sanitizeSkillsConfig } from './programSkills';

/* ─── Documents ───────────────────────────────────────────────────────────── */

export type DocumentValidationRule =
  | 'must-not-be-expired'
  | 'must-be-legible'
  | 'must-show-full-page'
  | 'must-match-passport-name'
  | 'must-be-recent-6-months'
  | 'must-be-signed'
  | 'must-be-official-translation';

export const DOCUMENT_VALIDATION_RULES: Array<{ id: DocumentValidationRule; label: string }> = [
  { id: 'must-not-be-expired', label: 'Must not be expired' },
  { id: 'must-be-legible', label: 'Must be fully legible' },
  { id: 'must-show-full-page', label: 'Full page / all corners visible' },
  { id: 'must-match-passport-name', label: 'Name must match passport' },
  { id: 'must-be-recent-6-months', label: 'Issued within the last 6 months' },
  { id: 'must-be-signed', label: 'Must be signed' },
  { id: 'must-be-official-translation', label: 'Certified translation required' },
];

export const DEFAULT_ACCEPTED_FORMATS = ['pdf', 'jpg', 'jpeg', 'png'];

/**
 * A required document is a first-class configurable object, not a string.
 * Admins control the label, guidance, whether it blocks submission, the file
 * types accepted, size limits, validation rules and display order.
 */
export interface ProgramDocument {
  id: string;
  /** Applicant-facing label, e.g. "Valid Passport". */
  label: string;
  /** Short helper text shown under the label. */
  description?: string;
  /** Longer instructions (rich text) shown in the upload drawer. */
  instructions?: string;
  /** false → applicant may submit without it. */
  required: boolean;
  /** Lowercase extensions without the dot. */
  acceptedFormats: string[];
  maxSizeMb?: number;
  validationRules: DocumentValidationRule[];
  /** Display order; the array order is authoritative, this mirrors it. */
  sortOrder: number;
  /** Hidden without deleting history. */
  archived?: boolean;
}

/* ─── Intakes (the pre-existing `terms` field) ────────────────────────────── */

/**
 * An intake window. NOTE: historically called "terms", which collided with
 * legal Terms & Conditions. The field name is preserved for data compatibility;
 * the type is named ProgramIntake to keep call sites unambiguous.
 */
export interface ProgramIntake {
  termId: string;
  label: string;
  startDate?: string;
  endDate?: string;
  applicationDeadline?: string;
  isOpen: boolean;
  capacity?: number;
  notes?: string;
}

/* ─── The program ─────────────────────────────────────────────────────────── */

export type PricingVariableVisibility = 'visible' | 'hidden' | 'disabled';

export interface ProgramPricingVariable {
  id: string;
  name: string;
  description?: string;
  sellingPrice?: number;
  costPrice?: number;
  code: string; // 5-digit unique code e.g. "10014"
  visibility: PricingVariableVisibility;
  currency?: string;
  sortOrder?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface ACDRecord {
  id: string;
  programId: string;
  pricingVariableId: string;
  sponsorId: string;
  sponsorName?: string;
  actualCost: number;
  invoiceTemplateId?: string;
  acdCode: string; // Unique entry code e.g. "ACD-1001"
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface StageNudgeConfig {
  emailTemplateId?: string;
  documentTemplateId?: string;
  documentTemplateIds?: string[];
}

export interface ProgramWelcomeEmailConfig {
  enabled: boolean;
  mode: 'template' | 'manual';
  templateId?: string;
  subject?: string;
  htmlBody?: string;
  attachments?: Array<{ name: string; base64Content: string; mimeType: string }>;
  documentTemplateIds?: string[];
}

export interface ProgramConfig {
  id: string;
  /** Mirror of `id` — some collections wrote `programId` instead. */
  programId: string;
  welcomeEmail?: ProgramWelcomeEmailConfig;
  workspaceId?: string;

  /* Identity */
  name: string;
  /** Unique 5-digit system-generated program code (e.g. "1001") */
  code?: string;
  active: boolean;
  sortOrder: number;

  /* Program Confirmation & Gate Type */
  confirmationType?: 'placement' | 'admission' | 'none';

  /* Workflow gates (master spec §6.2, §7.2, §8.2, §15.3) */
  /** Gate: invoice must exist before Immigration. Default true. */
  requiresInvoice?: boolean;
  /** Which visa outcome satisfies the Immigration gate. Default 'approved'. */
  qualifyingVisaOutcome?: 'approved' | 'issued';
  /** What counts as Service Delivered. Default 'arrivalConfirmed'. */
  deliveryDefinition?: 'arrivalConfirmed' | 'programStarted' | 'preDepartureComplete';
  /** e.g. 'DS-2019' for J-1, 'I-20' for Study Abroad. */
  immigrationDocumentType?: string;
  /** Partial-payment % that satisfies the finance gate. Default 100. */
  minimumToProceed?: number;
  /** Nudge email cadence in days, keyed by canonical stage. */
  nudgeFrequencyByStage?: Partial<Record<string, number>>;
  /** Workflow stage nudges (template selections) configured per stage/phase. */
  stageNudges?: Partial<Record<string, StageNudgeConfig>>;
  /** Daily auto-advance run time 'HH:mm'. */
  autoAdvanceTime?: string;

  /* Draft Deadline Configuration (exactly one must be defined) */
  timeToCompleteInDays?: number;
  timeToCompleteByDate?: string;

  /* Content — all rich text (HTML) unless noted */
  description: string;
  /** Long-form "about this program" body. */
  overview: string;
  /** Eligibility requirements. Legacy field name: `requirements`. */
  eligibility: string;
  /** Legal terms shown before submission. Empty → global default applies. */
  termsAndConditions: string;
  /** Applicant-facing instructions. */
  instructions: string;
  /** Internal-only processing notes (never rendered to applicants). */
  processingNotes: string;
  /**
   * Admin-only internal notes that GUIDE THE AI (2026-10-12): fed into the
   * AI eligibility evaluation (and any AI screening support) as admin
   * guidance for this program. NEVER exposed to applicants — it must not
   * appear in applicant-facing payloads or the applicant journey.
   */
  internalNotes: string;
  /** Marketing blurb used by referrer/partner surfaces. */
  marketingContent: string;
  /** AI document-review guide (pre-existing field). */
  documentGuide: string;

  /* Configuration */
  pricingVariables?: ProgramPricingVariable[];
  acdRecords?: ACDRecord[];
  requiredDocuments: ProgramDocument[];
  intakes: ProgramIntake[];
  pipeline: PipelineStage[];
  /** Stage id applied to a newly created application. */
  defaultStageId?: string;
  trainingResources: ProgramResource[];
  marketingResources: ProgramResource[];

  /* Constraints */
  eligibleCountries: string[];
  allowedServiceProviders: string[];
  allowedSponsors: string[];
  visaType?: string;
  minDurationWeeks?: number;
  maxDurationWeeks?: number;
  /** Wave 4 (Slice D D-13): minimum applicant age for this program.
   *  Unset → the platform default of 18 applies (see legalConfig). */
  minimumAge?: number;

  /* Skills & Abilities — this program's OWN predefined skill list and
   * selection rule (single / multiple with optional max; blank max =
   * unlimited). Skills never leak across programs. Optional on the type
   * because legacy literals construct ProgramConfig without it;
   * normaliseProgram always fills the default. */
  skillsConfig?: ProgramSkillsConfig;

  /* Metadata */
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  /** Anything the backend sends that this version does not model yet. */
  metadata: Record<string, unknown>;
}

/** Global/workspace-level fallbacks for content a program leaves blank. */
export interface ProgramDefaults {
  termsAndConditions?: string;
  instructions?: string;
  eligibility?: string;
  overview?: string;
  requiredDocuments?: ProgramDocument[];
  pipeline?: PipelineStage[];
}

/* ─── Helpers ─────────────────────────────────────────────────────────────── */

function newId(prefix: string): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().replace(/-/g, '').slice(0, 10)
      : Math.random().toString(36).slice(2, 12);
  return `${prefix}_${rand}`;
}

function asString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value == null) return '';
  return String(value);
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map(asString).map((v) => v.trim()).filter(Boolean);
}

function firstNonEmpty(...values: Array<unknown>): string {
  for (const value of values) {
    const str = asString(value).trim();
    if (str) return str;
  }
  return '';
}

/** Slug used as a stable id when the backend never assigned one. */
export function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

/* ─── Document normalisation ──────────────────────────────────────────────── */

/**
 * Accepts a legacy `string[]` (labels only) or the modern object form and
 * returns fully-populated ProgramDocument objects. Legacy labels become
 * required documents accepting the default formats — identical behaviour to
 * before, but now editable.
 */
export function normaliseDocuments(value: unknown): ProgramDocument[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((entry, index): ProgramDocument | null => {
      if (typeof entry === 'string') {
        const label = entry.trim();
        if (!label) return null;
        return {
          id: `doc_${slugify(label) || index}`,
          label,
          description: '',
          instructions: '',
          required: true,
          acceptedFormats: [...DEFAULT_ACCEPTED_FORMATS],
          validationRules: [],
          sortOrder: index,
        };
      }

      if (entry && typeof entry === 'object') {
        const raw = entry as Record<string, unknown>;
        const label = firstNonEmpty(raw.label, raw.name, raw.title, raw.documentType);
        if (!label) return null;
        const formats = asStringArray(raw.acceptedFormats ?? raw.formats ?? raw.fileTypes)
          .map((f) => f.replace(/^\./, '').toLowerCase());
        return {
          id: asString(raw.id) || `doc_${slugify(label) || index}`,
          label,
          description: asString(raw.description),
          instructions: asString(raw.instructions),
          required: raw.required === undefined ? raw.optional !== true : raw.required !== false,
          acceptedFormats: formats.length ? formats : [...DEFAULT_ACCEPTED_FORMATS],
          maxSizeMb: typeof raw.maxSizeMb === 'number' ? raw.maxSizeMb : undefined,
          validationRules: asStringArray(raw.validationRules) as DocumentValidationRule[],
          sortOrder: typeof raw.sortOrder === 'number' ? raw.sortOrder : index,
          archived: raw.archived === true,
        };
      }

      return null;
    })
    .filter((doc): doc is ProgramDocument => doc !== null)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((doc, index) => ({ ...doc, sortOrder: index }));
}

export function createDocument(label = 'New document'): ProgramDocument {
  return {
    id: newId('doc'),
    label,
    description: '',
    instructions: '',
    required: true,
    acceptedFormats: [...DEFAULT_ACCEPTED_FORMATS],
    validationRules: [],
    sortOrder: 0,
  };
}

/* ─── Intake normalisation ────────────────────────────────────────────────── */

/**
 * Accepts `string[]` (legacy labels, per src/types.ts) or `ProgramTerm[]`
 * (objects, per the old usePrograms hook). This mismatch was a live bug: the
 * application wizard iterated objects while the type said strings.
 */
export function normaliseIntakes(value: unknown): ProgramIntake[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((entry, index): ProgramIntake | null => {
      if (typeof entry === 'string') {
        const label = entry.trim();
        if (!label) return null;
        return { termId: `term_${slugify(label) || index}`, label, isOpen: true };
      }
      if (entry && typeof entry === 'object') {
        const raw = entry as Record<string, unknown>;
        const label = firstNonEmpty(raw.label, raw.name, raw.term);
        if (!label) return null;
        return {
          termId: asString(raw.termId) || asString(raw.id) || `term_${slugify(label) || index}`,
          label,
          startDate: asString(raw.startDate) || undefined,
          endDate: asString(raw.endDate) || undefined,
          applicationDeadline: asString(raw.applicationDeadline) || undefined,
          isOpen: raw.isOpen !== false,
          capacity: typeof raw.capacity === 'number' ? raw.capacity : undefined,
          notes: asString(raw.notes) || undefined,
        };
      }
      return null;
    })
    .filter((intake): intake is ProgramIntake => intake !== null);
}

/* ─── Program normalisation ───────────────────────────────────────────────── */

export function normaliseACDRecords(value: unknown): ACDRecord[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((entry, index): ACDRecord | null => {
      if (!entry || typeof entry !== 'object') return null;
      const raw = entry as Record<string, unknown>;
      const pricingVariableId = asString(raw.pricingVariableId || raw.variableId);
      const sponsorId = asString(raw.sponsorId || raw.partnerId || raw.supplierId);
      if (!pricingVariableId || !sponsorId) return null;

      const actualCost = typeof raw.actualCost === 'number' && Number.isFinite(raw.actualCost)
        ? raw.actualCost
        : Number(raw.actualCost) || 0;

      const id = asString(raw.id) || `acd_${Date.now()}_${index}`;
      const acdCode = asString(raw.acdCode || raw.code) || '';

      return {
        id,
        programId: asString(raw.programId),
        pricingVariableId,
        sponsorId,
        sponsorName: asString(raw.sponsorName) || undefined,
        actualCost,
        invoiceTemplateId: asString(raw.invoiceTemplateId) || undefined,
        acdCode,
        active: raw.active !== false,
        createdAt: asString(raw.createdAt) || undefined,
        updatedAt: asString(raw.updatedAt) || undefined,
      };
    })
    .filter((r): r is ACDRecord => r !== null);
}

export function normalisePricingVariables(value: unknown): ProgramPricingVariable[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((entry, index): ProgramPricingVariable | null => {
      if (!entry || typeof entry !== 'object') return null;
      const raw = entry as Record<string, unknown>;
      const name = firstNonEmpty(raw.name, raw.label, raw.title);
      if (!name) return null;

      const sellingPrice = typeof raw.sellingPrice === 'number' && Number.isFinite(raw.sellingPrice)
        ? raw.sellingPrice
        : typeof raw.price === 'number'
        ? raw.price
        : Number(raw.sellingPrice) || 0;

      const costPrice = typeof raw.costPrice === 'number' && Number.isFinite(raw.costPrice)
        ? raw.costPrice
        : Number(raw.costPrice) || 0;

      let visibility: PricingVariableVisibility = 'visible';
      if (raw.visibility === 'hidden' || raw.hidden === true || raw.active === false) {
        visibility = 'hidden';
      } else if (raw.visibility === 'disabled' || raw.disabled === true) {
        visibility = 'disabled';
      } else if (raw.visibility === 'visible') {
        visibility = 'visible';
      }

      const id = asString(raw.id) || asString(raw.variableId) || newId('var');
      const code = asString(raw.code) || '';

      const description = asString(raw.description) || undefined;

      return {
        id,
        name,
        description,
        sellingPrice,
        costPrice,
        code,
        visibility,
        currency: asString(raw.currency) || 'USD',
        sortOrder: typeof raw.sortOrder === 'number' ? raw.sortOrder : index,
        createdAt: asString(raw.createdAt) || undefined,
        updatedAt: asString(raw.updatedAt) || undefined,
      };
    })
    .filter((v): v is ProgramPricingVariable => v !== null)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

const KNOWN_KEYS = new Set([
  'id', 'programId', 'workspaceId', 'name', 'code', 'active', 'isOpen', 'sortOrder', 'welcomeEmail',
  'description', 'overview', 'programOverview', 'eligibility', 'requirements',
  'eligibilityRequirements', 'termsAndConditions', 'terms', 'termsHtml', 'instructions',
  'processingNotes', 'internalNotes', 'marketingContent', 'documentGuide',
  'pricingVariables', 'acdRecords', 'requiredDocuments', 'documents', 'intakes', 'pipeline', 'masterPipeline',
  'defaultStageId', 'trainingResources', 'trainingMaterials', 'marketingResources',
  'marketingMaterials', 'eligibleCountries', 'allowedServiceProviders', 'allowedSponsors', 'sponsorIds', 'visaType',
  'minDurationWeeks', 'maxDurationWeeks', 'minimumAge', 'skillsConfig', 'createdAt', 'updatedAt', 'createdBy', 'deletedAt',
  'confirmationType', 'timeToCompleteInDays', 'timeToCompleteByDate',
  'requiresInvoice', 'qualifyingVisaOutcome', 'deliveryDefinition',
  'immigrationDocumentType', 'minimumToProceed', 'nudgeFrequencyByStage', 'stageNudges', 'autoAdvanceTime',
]);

/**
 * Validates program draft deadline configuration.
 * Program settings must hold EXACTLY ONE of `timeToCompleteInDays` or `timeToCompleteByDate`.
 */
export function validateProgramDraftDeadline(data: Record<string, unknown>): { valid: boolean; error?: string } {
  const daysVal = data.timeToCompleteInDays;
  const dateVal = data.timeToCompleteByDate;

  const hasDays = daysVal !== undefined && daysVal !== null && daysVal !== '';
  const hasDate = dateVal !== undefined && dateVal !== null && dateVal !== '';

  if ((hasDays && hasDate) || (!hasDays && !hasDate)) {
    return {
      valid: false,
      error: 'Program settings must specify exactly one of timeToCompleteInDays or timeToCompleteByDate.',
    };
  }

  if (hasDays) {
    const num = Number(daysVal);
    if (isNaN(num) || num <= 0 || !Number.isInteger(num)) {
      return {
        valid: false,
        error: 'timeToCompleteInDays must be a positive whole integer number of days.',
      };
    }
  }

  if (hasDate) {
    const parsed = new Date(asString(dateVal));
    if (isNaN(parsed.getTime())) {
      return {
        valid: false,
        error: 'timeToCompleteByDate must be a valid ISO date string.',
      };
    }
  }

  return { valid: true };
}

/**
 * Computes the stamped application deadline (master spec §3.1).
 *
 * - Days mode: deadline = creation date + N days.
 * - Date mode: the configured future date.
 * - Intake-window rule: if the intake's closing date is earlier than the
 *   computed deadline, the stamped deadline is the intake closing date
 *   (the earlier of the two).
 *
 * The stamped deadline is fixed at creation and must NEVER be recomputed
 * from program settings later (changing a setting must not silently move
 * existing deadlines). A submission accepted before the deadline is never
 * invalidated because the deadline later passes.
 */
export function computeStampedDeadline(
  program: Pick<ProgramConfig, 'timeToCompleteInDays' | 'timeToCompleteByDate' | 'intakes'> | null | undefined,
  intakeTermId: string | undefined,
  createdAt: Date = new Date()
): string {
  let deadline: Date | null = null;

  if (program) {
    if (typeof program.timeToCompleteInDays === 'number' && program.timeToCompleteInDays > 0) {
      deadline = new Date(createdAt.getTime() + program.timeToCompleteInDays * 24 * 60 * 60 * 1000);
    } else if (program.timeToCompleteByDate) {
      const parsed = new Date(program.timeToCompleteByDate);
      if (!isNaN(parsed.getTime())) deadline = parsed;
    }
  }

  // Intake-window rule: earlier of computed deadline and intake closing date.
  if (intakeTermId && program?.intakes) {
    const intake = program.intakes.find((i) => i.termId === intakeTermId || i.label === intakeTermId);
    const closingRaw = intake?.applicationDeadline || intake?.endDate;
    if (closingRaw) {
      const closing = new Date(closingRaw);
      if (!isNaN(closing.getTime()) && (!deadline || closing < deadline)) {
        deadline = closing;
      }
    }
  }

  if (!deadline) {
    deadline = new Date(createdAt.getTime() + 14 * 24 * 60 * 60 * 1000);
  }
  return deadline.toISOString();
}

/**
 * The one and only way to turn a raw backend/Firestore record into a program.
 * Tolerates every legacy field name found in the audit so no screen has to.
 */
export function normaliseProgram(raw: unknown): ProgramConfig | null {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as Record<string, unknown>;

  const id = firstNonEmpty(data.id, data.programId);
  const name = firstNonEmpty(data.name, data.title, data.code, id);
  if (!id && !name) return null;

  const resolvedId = id || slugify(name);

  const metadata: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (!KNOWN_KEYS.has(key)) metadata[key] = value;
  }

  return {
    id: resolvedId,
    programId: resolvedId,
    workspaceId: asString(data.workspaceId) || undefined,

    name: name || resolvedId,
    code: asString(data.code) || undefined,
    // `active` is the modern flag; `isOpen` was the flag on the other model.
    active: data.active !== undefined ? data.active !== false : data.isOpen !== false,
    sortOrder: typeof data.sortOrder === 'number' ? data.sortOrder : 0,

    confirmationType: (['placement', 'admission', 'none'].includes(asString(data.confirmationType))
      ? asString(data.confirmationType)
      : 'placement') as 'placement' | 'admission' | 'none',
    timeToCompleteInDays: typeof data.timeToCompleteInDays === 'number' && data.timeToCompleteInDays > 0
      ? data.timeToCompleteInDays
      : undefined,
    timeToCompleteByDate: asString(data.timeToCompleteByDate) || undefined,

    requiresInvoice: data.requiresInvoice === undefined ? true : data.requiresInvoice !== false,
    qualifyingVisaOutcome: ['approved', 'issued'].includes(asString(data.qualifyingVisaOutcome))
      ? (asString(data.qualifyingVisaOutcome) as 'approved' | 'issued')
      : 'approved',
    deliveryDefinition: ['arrivalConfirmed', 'programStarted', 'preDepartureComplete'].includes(asString(data.deliveryDefinition))
      ? (asString(data.deliveryDefinition) as 'arrivalConfirmed' | 'programStarted' | 'preDepartureComplete')
      : 'arrivalConfirmed',
    immigrationDocumentType: asString(data.immigrationDocumentType) || undefined,
    minimumToProceed: typeof data.minimumToProceed === 'number' && data.minimumToProceed > 0 && data.minimumToProceed <= 100
      ? data.minimumToProceed
      : 100,
    nudgeFrequencyByStage: (typeof data.nudgeFrequencyByStage === 'object' && data.nudgeFrequencyByStage !== null
      ? data.nudgeFrequencyByStage
      : undefined) as Partial<Record<string, number>> | undefined,
    stageNudges: (typeof data.stageNudges === 'object' && data.stageNudges !== null
      ? data.stageNudges
      : undefined) as Partial<Record<string, StageNudgeConfig>> | undefined,
    autoAdvanceTime: asString(data.autoAdvanceTime) || undefined,

    description: asString(data.description),
    overview: firstNonEmpty(data.overview, data.programOverview),
    eligibility: firstNonEmpty(data.eligibility, data.eligibilityRequirements, data.requirements),
    termsAndConditions: firstNonEmpty(data.termsAndConditions, data.termsHtml),
    instructions: asString(data.instructions),
    processingNotes: asString(data.processingNotes),
    internalNotes: asString(data.internalNotes),
    marketingContent: asString(data.marketingContent),
    documentGuide: asString(data.documentGuide),
    welcomeEmail: (data.welcomeEmail && typeof data.welcomeEmail === 'object'
      ? {
          enabled: (data.welcomeEmail as any).enabled === true,
          mode: (data.welcomeEmail as any).mode === 'manual' ? 'manual' : 'template',
          templateId: asString((data.welcomeEmail as any).templateId) || undefined,
          subject: asString((data.welcomeEmail as any).subject) || undefined,
          htmlBody: asString((data.welcomeEmail as any).htmlBody) || undefined,
          attachments: Array.isArray((data.welcomeEmail as any).attachments) ? (data.welcomeEmail as any).attachments : [],
          documentTemplateIds: Array.isArray((data.welcomeEmail as any).documentTemplateIds) ? (data.welcomeEmail as any).documentTemplateIds : [],
        }
      : { enabled: false, mode: 'template' }),

    pricingVariables: normalisePricingVariables(data.pricingVariables),
    acdRecords: normaliseACDRecords(data.acdRecords),
    requiredDocuments: normaliseDocuments(data.requiredDocuments ?? data.documents),
    intakes: normaliseIntakes(data.intakes ?? data.terms),
    pipeline: normalisePipeline(data.pipeline ?? data.masterPipeline),
    defaultStageId: asString(data.defaultStageId) || undefined,
    trainingResources: normaliseResources(data.trainingResources ?? data.trainingMaterials, 'training'),
    marketingResources: normaliseResources(data.marketingResources ?? data.marketingMaterials, 'marketing'),

    eligibleCountries: asStringArray(data.eligibleCountries),
    allowedServiceProviders: asStringArray(data.allowedServiceProviders),
    allowedSponsors: asStringArray(data.allowedSponsors ?? data.sponsorIds),
    visaType: asString(data.visaType) || undefined,
    minDurationWeeks: typeof data.minDurationWeeks === 'number' ? data.minDurationWeeks : undefined,
    maxDurationWeeks: typeof data.maxDurationWeeks === 'number' ? data.maxDurationWeeks : undefined,
    minimumAge: typeof data.minimumAge === 'number' && Number.isFinite(data.minimumAge) && data.minimumAge >= 0 ? data.minimumAge : undefined,
    skillsConfig: sanitizeSkillsConfig(data.skillsConfig),

    createdAt: asString(data.createdAt) || undefined,
    updatedAt: asString(data.updatedAt) || undefined,
    createdBy: asString(data.createdBy) || undefined,
    metadata,
  };
}

export function normaliseProgramList(raw: unknown): ProgramConfig[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map(normaliseProgram)
    .filter((p): p is ProgramConfig => p !== null)
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || a.name.localeCompare(b.name));
}

/**
 * Converts a ProgramConfig back to the persistence shape.
 * Legacy mirrors (`requirements`, `terms`, `trainingMaterials`,
 * `marketingMaterials`) are written alongside the new fields so older readers
 * that have not been migrated keep working during rollout.
 */
export function serialiseProgram(program: ProgramConfig): Record<string, unknown> {
  return {
    ...program.metadata,
    id: program.id,
    programId: program.id,
    workspaceId: program.workspaceId,
    name: program.name,
    code: program.code,
    active: program.active,
    isOpen: program.active,
    sortOrder: program.sortOrder,

    description: program.description,
    overview: program.overview,
    eligibility: program.eligibility,
    requirements: program.eligibility, // legacy mirror
    termsAndConditions: program.termsAndConditions,
    instructions: program.instructions,
    processingNotes: program.processingNotes,
    internalNotes: program.internalNotes,
    marketingContent: program.marketingContent,
    documentGuide: program.documentGuide,

    pricingVariables: program.pricingVariables || [],
    acdRecords: program.acdRecords || [],
    requiredDocuments: program.requiredDocuments,
    terms: program.intakes, // legacy field name for intakes
    intakes: program.intakes,
    pipeline: program.pipeline,
    defaultStageId: program.defaultStageId,
    trainingResources: program.trainingResources,
    trainingMaterials: program.trainingResources, // legacy mirror
    marketingResources: program.marketingResources,
    marketingMaterials: program.marketingResources, // legacy mirror

    eligibleCountries: program.eligibleCountries,
    allowedServiceProviders: program.allowedServiceProviders,
    allowedSponsors: program.allowedSponsors,
    sponsorIds: program.allowedSponsors, // legacy mirror
    visaType: program.visaType,
    minDurationWeeks: program.minDurationWeeks,
    maxDurationWeeks: program.maxDurationWeeks,
    minimumAge: program.minimumAge,
    skillsConfig: sanitizeSkillsConfig(program.skillsConfig),
    confirmationType: program.confirmationType || 'placement',
    timeToCompleteInDays: program.timeToCompleteInDays,
    timeToCompleteByDate: program.timeToCompleteByDate,
    requiresInvoice: program.requiresInvoice !== false,
    qualifyingVisaOutcome: program.qualifyingVisaOutcome || 'approved',
    deliveryDefinition: program.deliveryDefinition || 'arrivalConfirmed',
    immigrationDocumentType: program.immigrationDocumentType,
    minimumToProceed: program.minimumToProceed || 100,
    nudgeFrequencyByStage: program.nudgeFrequencyByStage,
    stageNudges: program.stageNudges,
    autoAdvanceTime: program.autoAdvanceTime,
    updatedAt: new Date().toISOString(),
  };
}

export function createProgram(name = 'New program'): ProgramConfig {
  return {
    id: newId('prog'),
    programId: newId('prog'),
    name,
    active: true,
    sortOrder: 0,
    description: '',
    overview: '',
    eligibility: '',
    termsAndConditions: '',
    instructions: '',
    processingNotes: '',
    internalNotes: '',
    marketingContent: '',
    documentGuide: '',
    pricingVariables: [],
    acdRecords: [],
    requiredDocuments: [],
    intakes: [],
    pipeline: [],
    trainingResources: [],
    marketingResources: [],
    eligibleCountries: [],
    allowedServiceProviders: [],
    allowedSponsors: [],
    skillsConfig: { selectionMode: 'multiple', maxSelections: null, skills: [] },
    metadata: {},
  };
}

/* ─── Resolvers: program → workspace default → global default ─────────────── */

/**
 * Terms & Conditions with the documented fallback chain. A screen calling this
 * can never render blank terms, which was requirement #3 of the overhaul.
 */
export function resolveTermsAndConditions(
  program: ProgramConfig | null | undefined,
  defaults?: ProgramDefaults
): { content: string; source: 'program' | 'default' | 'none' } {
  const programTerms = program?.termsAndConditions?.trim();
  if (programTerms) return { content: programTerms, source: 'program' };

  const fallback = defaults?.termsAndConditions?.trim();
  if (fallback) return { content: fallback, source: 'default' };

  return { content: '', source: 'none' };
}

export function resolveEligibility(
  program: ProgramConfig | null | undefined,
  defaults?: ProgramDefaults
): string {
  return program?.eligibility?.trim() || defaults?.eligibility?.trim() || '';
}

export function resolveOverview(
  program: ProgramConfig | null | undefined,
  defaults?: ProgramDefaults
): string {
  return program?.overview?.trim() || program?.description?.trim() || defaults?.overview?.trim() || '';
}

export function resolveInstructions(
  program: ProgramConfig | null | undefined,
  defaults?: ProgramDefaults
): string {
  return program?.instructions?.trim() || defaults?.instructions?.trim() || '';
}

/**
 * Required documents for a program, excluding archived entries.
 * Falls back to the workspace default list when the program defines none.
 */
export function resolveRequiredDocuments(
  program: ProgramConfig | null | undefined,
  defaults?: ProgramDefaults
): ProgramDocument[] {
  const own = (program?.requiredDocuments || []).filter((doc) => !doc.archived);
  if (own.length) return own;
  return (defaults?.requiredDocuments || []).filter((doc) => !doc.archived);
}

/** Convenience for the many places that still only need labels. */
export function requiredDocumentLabels(
  program: ProgramConfig | null | undefined,
  defaults?: ProgramDefaults
): string[] {
  return resolveRequiredDocuments(program, defaults)
    .filter((doc) => doc.required)
    .map((doc) => doc.label);
}

export function resolveOpenIntakes(program: ProgramConfig | null | undefined): ProgramIntake[] {
  return (program?.intakes || []).filter((intake) => intake.isOpen);
}

/** Find a program by id, programId, code or (last resort) exact name. */
export function findProgram(
  programs: ProgramConfig[],
  identifier: string | null | undefined
): ProgramConfig | null {
  if (!identifier) return null;
  const needle = identifier.trim().toLowerCase();
  if (!needle) return null;
  return (
    programs.find((p) => p.id.toLowerCase() === needle) ||
    programs.find((p) => p.programId.toLowerCase() === needle) ||
    programs.find((p) => (p.code || '').toLowerCase() === needle) ||
    programs.find((p) => p.name.toLowerCase() === needle) ||
    null
  );
}

/**
 * Programs visible in a workspace. Records without a workspaceId are treated as
 * global/legacy and remain visible, so existing tenants do not lose their
 * catalogue during migration — but a record belonging to ANOTHER workspace is
 * never returned.
 */
export function filterByWorkspace(
  programs: ProgramConfig[],
  workspaceId: string | null | undefined
): ProgramConfig[] {
  if (!workspaceId) return programs;
  return programs.filter((p) => !p.workspaceId || p.workspaceId === workspaceId);
}

/* ─── Legacy interop ──────────────────────────────────────────────────────── */

/**
 * Projection back to the historical `Program` shape from `src/types.ts`
 * (`requiredDocuments: string[]`, `terms`, `requirements`).
 *
 * Screens are being migrated to `ProgramConfig` incrementally; until then this
 * lets a caller feed normalised, workspace-scoped data into components that
 * still expect the old fields — WITHOUT reintroducing a second source of truth,
 * because the values still originate from the program record.
 */
export function toLegacyProgram(program: ProgramConfig): {
  id: string;
  programId: string;
  name: string;
  code?: string;
  description: string;
  requiredDocuments: string[];
  /**
   * The configured documents as OBJECTS (required flag + description
   * survive). The label-only `requiredDocuments` projection above loses
   * the required flag — consumers that badge or gate on required-ness
   * (ApplicantFlow Step 7, the required-set builder) MUST read this
   * field, or every document silently becomes "required" again when
   * the labels are re-normalised (the Oct 12 live bug: 11 documents all
   * badged REQUIRED TO SUBMIT and a 17-document server gate).
   */
  requiredDocumentsDetailed: ProgramDocument[];
  active: boolean;
  requirements: string;
  terms: ProgramIntake[];
  documentGuide: string;
  /** Wave 4: applicant-facing program terms + age gate inputs. Without
   *  these the legacy shape hid both from ApplicantFlow entirely. */
  termsAndConditions: string;
  minimumAge?: number;
  skillsConfig: ProgramSkillsConfig;
} {
  return {
    id: program.id,
    programId: program.programId,
    name: program.name,
    code: program.code,
    description: program.description,
    skillsConfig: sanitizeSkillsConfig(program.skillsConfig),
    requiredDocuments: program.requiredDocuments
      .filter((doc) => !doc.archived)
      .map((doc) => doc.label),
    requiredDocumentsDetailed: program.requiredDocuments.filter((doc) => !doc.archived),
    active: program.active,
    requirements: program.eligibility,
    terms: program.intakes,
    documentGuide: program.documentGuide,
    termsAndConditions: program.termsAndConditions,
    minimumAge: program.minimumAge,
  };
}
